import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import useLocales from 'hooks/useLocales';
import { Form, Formik } from 'formik';
import { JobService } from 'services/jobs/job';
import Autocomplete from 'components/shared/Autocomplete/Index';
import DropDown from 'components/Dropdown';
import { ProfileService } from 'services/profile/profile';
import { degreeTypes } from 'core/constants/aboutMePopUpConstants';
import { SkillService } from 'services/skills/skillService';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';
import {
  getCareerLevelLabels,
  getSalaryLabels,
  YES_NO_MAYBE_CHOICES,
  AVAIL_CHOICES,
  MANAGEMENT_EXP_CHOICES
} from 'constants/constants';
import Image from 'components/image/Image';
import Checkboxes from 'components/shared/Checkbox/Index';
import Multiselect from 'components/shared/Multiselect/Index';
import { initialValues } from './types';
import { LanguageService } from 'services/language';
import { ICandidateLanguage } from 'core/interface/candidate-language';
import { contractTypes } from 'core/constants/workType';
import { hourlyRateChoices } from 'core/constants/preferenceConstats';
import { JobCitiesResultsType } from 'core/interface/job';
import { useDebounce } from 'helpers/useDebounce';

import './index.scss';
import RadioButton from 'components/RadioGroup/Index';
import { OptionType } from 'core/interface/autocomplete';

// Stable empty value — selected locations render as chips below the input
const EMPTY_LOCATION_VALUE: OptionType[] = [];

const MatchFilter = ({ applyFilter }: any): JSX.Element => {
  const { t } = useLocales();
  const [initialValue, setInitialValue] = useState<any>({
    industry: '',
    studies: [],
    degree_type: '',
    locations: [],
    compensations: [],
    career_level: [],
    languages: [],
    is_remote: false,
    contracts: [],
    skills: [],
    relocations: [],
    availabilities: [],
    management_exps: []
  });

  const { languages } = useSelector((state: RootState) => state.languages);
  const [formKey, setFormKey] = useState<number>(0);
  const { computerSkills: skills } = useSelector(
    (state: RootState) => state.computerSkills
  );
  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );

  const { schools } = useSelector((state: RootState) => state.schools);

  const [locationOptions, setLocationOptions] = useState<
    JobCitiesResultsType[]
  >([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [selectedLocationLabels, setSelectedLocationLabels] = useState<
    Record<string, string>
  >({});
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const debouncedLocationQuery = useDebounce(locationQuery, 500);
  const skipApplyOnMount = useRef(true);

  // Instant apply — no Filter button (skip initial mount)
  useEffect(() => {
    if (skipApplyOnMount.current) {
      skipApplyOnMount.current = false;
      return;
    }
    applyFilter(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (!debouncedLocationQuery.trim()) {
      setLocationOptions([]);
      return;
    }

    let cancelled = false;
    setIsLoadingLocations(true);
    JobService.getLocations({ q: debouncedLocationQuery })
      .then((res) => {
        if (!cancelled) {
          setLocationOptions(res?.results || []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingLocations(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedLocationQuery]);

  const getSchools = (query: string = '') => {
    if (!query.trim()) {
      return;
    }
    ProfileService.getSchools(query);
  };

  const getSkills = async (query?: string): Promise<void> => {
    if (!query?.trim()) {
      return;
    }
    SkillService.getITSkillsList(query);
  };

  const getIndustries = (query?: string): void => {
    if (!query?.trim()) {
      return;
    }
    ProfileService.getIndustries(query);
  };

  const handleCheckbox = (e: string, key: string): void => {
    const current = [...(initialValue[key] as any[])];
    const index = current.indexOf(e);
    if (index !== -1) {
      current.splice(index, 1);
    } else {
      current.push(e);
    }

    setInitialValue({
      ...initialValue,
      [key]: current
    });
  };

  const getLanguages = (q?: string) => {
    if (!q?.trim()) {
      return;
    }
    LanguageService.getAllLanguages(q);
  };

  const handleRemoveLanguage = (item: ICandidateLanguage): void => {
    setInitialValue({
      ...initialValue,
      languages: initialValue?.languages.filter(
        (e: ICandidateLanguage) => e.id !== item.id
      )
    });
  };

  const handleResetChanges = (): void => {
    setFormKey(Math.random());
    setLocationQuery('');
    setLocationOptions([]);
    setSelectedLocationLabels({});
    setInitialValue({
      industry: '',
      studies: [],
      degree_type: '',
      locations: [],
      compensations: [],
      career_level: [],
      languages: [] as ICandidateLanguage[],
      is_remote: false,
      contracts: [],
      skills: [],
      relocations: [],
      availabilities: [],
      management_exps: []
    });
  };

  const renderCompensationSection = () => {
    return (
      <div className="input-container">
        <span className="checkbox-label">{t('Compensations (EUR)')}</span>

        <h3 className="section-header">{t('Monthly Salary')}</h3>
        {getSalaryLabels(initialValue?.compensations).map((e, i) => (
          <Checkboxes
            checkboxes={e}
            onArrayChange={(e) => {
              if (Array.isArray(e)) {
                handleCheckbox(e[0].value ?? e[0].label, 'compensations');
              }
            }}
            key={`monthly-${i}`}
          />
        ))}

        <h3 className="section-header">{t('Hourly Rate')}</h3>
        {hourlyRateChoices.map((choice, i) => (
          <Checkboxes
            checkboxes={[
              {
                label: String(choice.to_show),
                isChecked: initialValue?.compensations?.includes(
                  String(choice.value)
                ),
                value: String(choice.value)
              }
            ]}
            onArrayChange={(e) => {
              if (Array.isArray(e)) {
                handleCheckbox(e[0].value ?? e[0].label, 'compensations');
              }
            }}
            key={`hourly-${i}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="match-filter">
      <p className="title">{t('Filter')}</p>
      <div className="form" key={formKey}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values, actions) => {}}
        >
          <Form>
            <>
              <div className="input-container">
                <span className="label">{t('Industry')}</span>
                <Autocomplete
                  fieldName="industry"
                  value={
                    industries?.find(
                      (industry) =>
                        String(industry?.id) === String(initialValue?.industry)
                    )?.name ?? ''
                  }
                  options={
                    industries?.map((e) => ({
                      id: e?.id,
                      title: e?.name
                    })) || []
                  }
                  show_location={false}
                  show_arrow={false}
                  placeholder={t('Type to search…')}
                  onChange={(e) =>
                    setInitialValue((prev: any) => ({
                      ...prev,
                      industry: String(
                        industries?.find((industry) => industry.name === e)
                          ?.id || ''
                      )
                    }))
                  }
                  onInputChange={(e) => {
                    if (e?.trim()) {
                      getIndustries(e);
                    }
                  }}
                  values={industries}
                />
              </div>
              <div className="input-container">
                <span className="label">{t('Education')}</span>
                <Multiselect
                  fieldName="studies"
                  value={initialValue?.studies
                    .map((id: string) => {
                      const school = schools?.find((s) => String(s.id) === id);
                      return school
                        ? {
                            id: school.id,
                            title: school.name,
                            name: school.name
                          }
                        : null;
                    })
                    .filter(Boolean)}
                  options={
                    schools?.map((e: any) => ({
                      id: e?.id,
                      title: e?.name,
                      name: e?.name
                    })) || []
                  }
                  show_location={false}
                  show_arrow={false}
                  placeholder={t('Type to search…')}
                  onChange={(e) => {
                    if (typeof e !== 'string' && Array.isArray(e)) {
                      const newStudies = e
                        .map((school) => {
                          if (typeof school === 'object' && 'id' in school) {
                            return String(school.id);
                          }
                          const foundSchool = schools?.find(
                            (s) => s.name === school
                          );
                          return String(foundSchool?.id || '');
                        })
                        .filter(
                          (id) =>
                            id !== '' && !initialValue.studies.includes(id)
                        );

                      setInitialValue((prev: any) => ({
                        ...prev,
                        studies: [...prev.studies, ...newStudies]
                      }));
                    }
                  }}
                  onInputChange={(e) => {
                    if (e?.trim()) {
                      getSchools(e);
                    }
                  }}
                />

                <div className="selected-skills">
                  {initialValue?.studies.map((id: string) => {
                    const school = schools?.find((s) => String(s.id) === id);
                    return school ? (
                      <div key={id} className="skill">
                        <div className="skill-name">{school.name}</div>
                        <div
                          className="remove"
                          onClick={() => {
                            setInitialValue((prev: any) => ({
                              ...prev,
                              studies: prev.studies.filter(
                                (schoolId: string) => schoolId !== id
                              )
                            }));
                          }}
                        >
                          <Image name="close.svg" width={10} height={10} />
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="input-container">
                <span className="label">{t('Degree Type')}</span>
                <DropDown
                  dropdownClassName="dropdown"
                  items={degreeTypes}
                  selectedValue={(e) =>
                    setInitialValue((prev: any) => ({
                      ...prev,
                      degree_type: e
                    }))
                  }
                  defaultValue={initialValue?.degree_type}
                  placeholder={t('Degree Type')}
                />
              </div>
              <div className="input-container">
                <span className="label">{t('Skills')}</span>
                <Multiselect
                  fieldName="skills"
                  value={initialValue?.skills}
                  options={
                    skills?.map((e: IAutocompleteItem) => ({
                      id: e?.id,
                      title: e?.name,
                      name: e?.name,
                      isComputerSkill: true
                    })) || []
                  }
                  show_location={false}
                  show_arrow={false}
                  placeholder={t('Type to search…')}
                  freeSolo={true}
                  onChange={(value) => {
                    if (!Array.isArray(value)) return;

                    const newSkills = value
                      .map((item) => {
                        // Case 1: Direct string (from freeSolo/handleKeyPress)
                        if (typeof item === 'string') {
                          // Check if it matches a computer skill
                          const matchedSkill = skills?.find(
                            (s) => s.name.toLowerCase() === item.toLowerCase()
                          );
                          if (matchedSkill) {
                            return {
                              id: matchedSkill.id,
                              title: matchedSkill.name,
                              name: matchedSkill.name,
                              isComputerSkill: true
                            };
                          }
                          // Free-form skill
                          return {
                            id: item,
                            title: item,
                            name: item,
                            isComputerSkill: false
                          };
                        }

                        // Case 2: Object (from dropdown or existing selection)
                        if (item && typeof item === 'object') {
                          // If it's already in the correct format, return as is
                          if ('isComputerSkill' in item) {
                            return item;
                          }
                          // If it's from dropdown but missing isComputerSkill
                          if ('id' in item) {
                            return {
                              ...item,
                              isComputerSkill: true
                            };
                          }
                        }

                        // Fallback case (shouldn't happen)
                        return null;
                      })
                      .filter(Boolean);

                    // Remove duplicates based on id for computer skills and title for free-form
                    const uniqueSkills = newSkills.reduce(
                      (acc: any[], skill) => {
                        const isDuplicate = acc.some((existing) =>
                          skill.isComputerSkill
                            ? String(existing.id) === String(skill.id)
                            : existing.title === skill.title
                        );
                        if (!isDuplicate) {
                          acc.push(skill);
                        }
                        return acc;
                      },
                      []
                    );

                    setInitialValue((prev: typeof initialValue) => ({
                      ...prev,
                      skills: uniqueSkills
                    }));
                  }}
                  onInputChange={(e) => {
                    if (e?.trim()) {
                      getSkills(e);
                    }
                  }}
                />

                <div className="selected-skills">
                  {initialValue?.skills?.map((skill: any, index: number) => (
                    <div
                      key={skill.isComputerSkill ? skill.id : `free-${index}`}
                      className="skill"
                    >
                      <div className="skill-name">{skill.name}</div>
                      <div
                        className="remove"
                        onClick={() => {
                          setInitialValue((prev: any) => ({
                            ...prev,
                            skills: prev.skills.filter((s: any) =>
                              skill.isComputerSkill
                                ? String(s.id) !== String(skill.id)
                                : s.title !== skill.title
                            )
                          }));
                        }}
                      >
                        <Image name="close.svg" width={10} height={10} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="input-container">
                <span className="label">{t('City')}</span>
                <Multiselect
                  fieldName="locations"
                  value={EMPTY_LOCATION_VALUE}
                  options={
                    locationOptions.map((e) => ({
                      id: e?.id,
                      title: e?.display_name,
                      name: e?.display_name
                    })) || []
                  }
                  show_location={true}
                  show_arrow={false}
                  placeholder={t('Type to search…')}
                  onChange={(e) => {
                    if (typeof e !== 'string' && Array.isArray(e)) {
                      const labelUpdates: Record<string, string> = {};
                      const newLocations = e
                        .map((location) => {
                          if (
                            typeof location === 'object' &&
                            'id' in location
                          ) {
                            const id = String(location.id);
                            const name =
                              (location as any).name ||
                              (location as any).title ||
                              locationOptions.find((c) => String(c.id) === id)
                                ?.display_name ||
                              '';
                            if (name) {
                              labelUpdates[id] = name;
                            }
                            return id;
                          }
                          const foundLocation = locationOptions.find(
                            (c) => c.display_name === location
                          );
                          if (foundLocation) {
                            labelUpdates[String(foundLocation.id)] =
                              foundLocation.display_name;
                            return String(foundLocation.id);
                          }
                          return '';
                        })
                        .filter(
                          (id) =>
                            id !== '' && !initialValue.locations.includes(id)
                        );

                      setSelectedLocationLabels((prev) => ({
                        ...prev,
                        ...labelUpdates
                      }));
                      setInitialValue((prev: any) => ({
                        ...prev,
                        locations: [...prev.locations, ...newLocations]
                      }));
                    }
                  }}
                  onInputChange={(e) => setLocationQuery(e)}
                />

                <div className="selected-skills">
                  {initialValue?.locations.map((id: string) => {
                    const label =
                      selectedLocationLabels[id] ||
                      locationOptions.find((c) => String(c.id) === id)
                        ?.display_name;
                    return label ? (
                      <div key={id} className="skill">
                        <div className="skill-name">{label}</div>
                        <div
                          className="remove"
                          onClick={() => {
                            setSelectedLocationLabels((prev) => {
                              const next = { ...prev };
                              delete next[id];
                              return next;
                            });
                            setInitialValue((prev: any) => ({
                              ...prev,
                              locations: prev.locations.filter(
                                (locationId: string) => locationId !== id
                              )
                            }));
                          }}
                        >
                          <Image name="close.svg" width={10} height={10} />
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="input-container">
                <span className="checkbox-label">{t('Contract Type')}</span>
                {contractTypes.map((contract, i) => (
                  <Checkboxes
                    key={i}
                    checkboxes={[
                      {
                        label: String(contract.to_show),
                        value: String(contract.value),
                        isChecked: initialValue.contracts.includes(
                          String(contract.value)
                        )
                      }
                    ]}
                    onArrayChange={(e) => {
                      if (
                        Array.isArray(e) &&
                        e[0] &&
                        (e[0].value || e[0].label)
                      ) {
                        const value = e[0].value
                          ? String(e[0].value)
                          : e[0].label;
                        handleCheckbox(value, 'contracts');
                      }
                    }}
                  />
                ))}
              </div>
              {renderCompensationSection()}
              <div className="input-container">
                <span className="checkbox-label">{t('Career level')}</span>
                {getCareerLevelLabels(initialValue?.career_level).map(
                  (e, i) => (
                    <Checkboxes
                      checkboxes={e}
                      onArrayChange={(e) => {
                        if (Array.isArray(e)) {
                          handleCheckbox(
                            e[0].value ?? e[0].label,
                            'career_level'
                          );
                        }
                      }}
                      key={i}
                    />
                  )
                )}
              </div>

              {/* New filter for relocations */}
              <div className="input-container">
                <span className="checkbox-label">{t('Relocations')}</span>
                {YES_NO_MAYBE_CHOICES.map((option, i) => (
                  <Checkboxes
                    key={i}
                    checkboxes={[
                      {
                        label: String(option.to_show),
                        value: String(option.value),
                        isChecked: initialValue.relocations.includes(
                          String(option.value)
                        )
                      }
                    ]}
                    onArrayChange={(e) => {
                      if (
                        Array.isArray(e) &&
                        e[0] &&
                        (e[0].value || e[0].label)
                      ) {
                        const value = e[0].value
                          ? String(e[0].value)
                          : e[0].label;
                        handleCheckbox(value, 'relocations');
                      }
                    }}
                  />
                ))}
              </div>

              {/* New filter for availabilities */}
              <div className="input-container">
                <span className="checkbox-label">{t('Availability')}</span>
                {AVAIL_CHOICES.map((option, i) => (
                  <Checkboxes
                    key={i}
                    checkboxes={[
                      {
                        label: String(option.to_show),
                        value: String(option.value),
                        isChecked: initialValue.availabilities.includes(
                          String(option.value)
                        )
                      }
                    ]}
                    onArrayChange={(e) => {
                      if (
                        Array.isArray(e) &&
                        e[0] &&
                        (e[0].value || e[0].label)
                      ) {
                        const value = e[0].value
                          ? String(e[0].value)
                          : e[0].label;
                        handleCheckbox(value, 'availabilities');
                      }
                    }}
                  />
                ))}
              </div>

              {/* New filter for management experience */}
              <div className="input-container">
                <span className="checkbox-label">
                  {t('Management Experience')}
                </span>
                {MANAGEMENT_EXP_CHOICES.map((option, i) => (
                  <Checkboxes
                    key={i}
                    checkboxes={[
                      {
                        label: String(option.to_show),
                        value: String(option.value),
                        isChecked: initialValue.management_exps.includes(
                          String(option.value)
                        )
                      }
                    ]}
                    onArrayChange={(e) => {
                      if (
                        Array.isArray(e) &&
                        e[0] &&
                        (e[0].value || e[0].label)
                      ) {
                        const value = e[0].value
                          ? String(e[0].value)
                          : e[0].label;
                        handleCheckbox(value, 'management_exps');
                      }
                    }}
                  />
                ))}
              </div>

              <div className="input-container">
                <Multiselect
                  options={
                    languages
                      ?.map((e) => ({
                        id: e?.id,
                        title: e?.name
                      }))
                      .filter(
                        (language) =>
                          // Filter out languages that are already selected
                          !initialValue.languages.some(
                            (selectedLang: ICandidateLanguage) =>
                              selectedLang.id === language.id
                          )
                      ) || []
                  }
                  show_location={false}
                  show_arrow={false}
                  placeholder={t('Type to search…')}
                  onInputChange={(e) => {
                    if (e?.trim()) {
                      getLanguages(e);
                    }
                  }}
                  onChange={(e: any) => {
                    if (typeof e != 'string') {
                      // Filter out duplicates based on language id
                      const uniqueLanguages = e.reduce(
                        (
                          acc: ICandidateLanguage[],
                          lang: ICandidateLanguage
                        ) => {
                          const isDuplicate = acc.some(
                            (item) => item.id === lang.id
                          );
                          if (!isDuplicate) {
                            acc.push(lang);
                          }
                          return acc;
                        },
                        []
                      );

                      setInitialValue({
                        ...initialValue,
                        languages: uniqueLanguages
                      });
                    }
                  }}
                  value={initialValue?.languages}
                  fieldName="language"
                />

                <div className="languages-container">
                  {initialValue?.languages.map((e: any) => (
                    <div key={e.id} className="language">
                      <div className="language-name">{e?.title}</div>
                      <div
                        className="remove"
                        onClick={() => handleRemoveLanguage(e)}
                      >
                        <Image name="close.svg" width={10} height={10} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="input-container">
                <span className="label">{t('Remote work only')}</span>
                <RadioButton
                  buttons={[
                    {
                      label: t('Yes'),
                      value: '1',
                      isChecked: initialValue?.is_remote
                    },
                    {
                      label: t('No'),
                      value: '0',
                      isChecked: !initialValue?.is_remote
                    }
                  ]}
                  onChange={(e) => {
                    setInitialValue((prev: typeof initialValue) => ({
                      ...prev,
                      is_remote: e === '1' ? true : false
                    }));
                  }}
                />
              </div>
              <div onClick={handleResetChanges} className="reset">
                {t('Reset Filters')}
              </div>
            </>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export default MatchFilter;
